// ===========================================
// Streams Management Page
// ===========================================
// CRUD interface for managing class streams (e.g. Leisure, Academy) with
// drag-and-drop reordering. A stream can override the hybrid 1:1 clinic duration.

import { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Alert,
  Chip,
  FormControlLabel,
  Switch,
  Stack,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Typography,
  Skeleton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import PageHeader from '../../components/common/PageHeader';
import FormModal from '../../components/common/FormModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ColorPickerField from '../../components/common/ColorPickerField';
import {
  useStreams,
  useCreateStream,
  useUpdateStream,
  useDeleteStream,
  useReorderStreams,
} from '../../hooks/useAdmin';
import { Stream } from '../../api/admin.api';

// ===========================================
// SORTABLE ITEM COMPONENT
// ===========================================

interface SortableStreamItemProps {
  stream: Stream;
  onEdit: (stream: Stream) => void;
  onDelete: (stream: Stream) => void;
}

function SortableStreamItem({ stream, onEdit, onDelete }: SortableStreamItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: stream.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const canDelete = !stream.isActive;

  // Format clinic duration for display
  const getDurationLabel = () => {
    if (stream.individualSlotDuration !== null) {
      return `${stream.individualSlotDuration} min 1:1 clinics`;
    }
    return 'Inherits each class’s clinic duration';
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          mr: 2,
          color: 'text.secondary',
          '&:active': {
            cursor: 'grabbing',
          },
        }}
      >
        <DragIcon />
      </Box>
      {stream.color && (
        <Box
          sx={{
            width: 24,
            height: 24,
            bgcolor: stream.color,
            borderRadius: 0.5,
            border: '1px solid',
            borderColor: 'divider',
            mr: 1.5,
            flexShrink: 0,
          }}
        />
      )}
      <ListItemText
        primary={stream.name}
        secondary={getDurationLabel()}
        sx={{ flex: 1 }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {stream.isActive ? (
          <Chip label="Active" size="small" color="success" />
        ) : (
          <Chip label="Inactive" size="small" color="default" />
        )}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit(stream)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={canDelete ? 'Delete' : 'Set status to Inactive to delete this stream'}>
            <span>
              <IconButton
                size="small"
                onClick={() => canDelete && onDelete(stream)}
                color={canDelete ? 'error' : 'default'}
                disabled={!canDelete}
                sx={{
                  opacity: canDelete ? 1 : 0.5,
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </ListItem>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function StreamsPage() {
  const { data: streamsData, isLoading, error } = useStreams();
  const createMutation = useCreateStream();
  const updateMutation = useUpdateStream();
  const deleteMutation = useDeleteStream();
  const reorderMutation = useReorderStreams();

  // Get streams array from response
  const streams = streamsData ?? [];

  // Filter state
  const [hideInactive, setHideInactive] = useState(false);

  // Form state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStream, setEditingStream] = useState<Stream | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    individualSlotDuration: '',
    isActive: true,
    color: null as string | null,
  });

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [streamToDelete, setStreamToDelete] = useState<Stream | null>(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filter streams
  const filteredStreams = useMemo((): Stream[] => {
    if (hideInactive) {
      return streams.filter((stream: Stream) => stream.isActive);
    }
    return streams;
  }, [streams, hideInactive]);

  // Handlers
  const handleAdd = () => {
    setEditingStream(null);
    setFormData({ name: '', individualSlotDuration: '', isActive: true, color: null });
    setModalOpen(true);
  };

  const handleEdit = (stream: Stream) => {
    setEditingStream(stream);
    setFormData({
      name: stream.name,
      individualSlotDuration: stream.individualSlotDuration !== null ? String(stream.individualSlotDuration) : '',
      isActive: stream.isActive,
      color: stream.color,
    });
    setModalOpen(true);
  };

  const handleDelete = (stream: Stream) => {
    setStreamToDelete(stream);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        name: formData.name,
        // Empty string => null (inherit the class's own clinic duration).
        individualSlotDuration: formData.individualSlotDuration
          ? parseInt(formData.individualSlotDuration, 10)
          : null,
        isActive: formData.isActive,
        color: formData.color,
      };

      if (editingStream) {
        await updateMutation.mutateAsync({
          id: editingStream.id,
          data,
        });
      } else {
        await createMutation.mutateAsync(data);
      }
      setModalOpen(false);
    } catch {
      // Error is handled by mutation
    }
  };

  const handleConfirmDelete = async () => {
    if (streamToDelete) {
      try {
        await deleteMutation.mutateAsync(streamToDelete.id);
        setDeleteDialogOpen(false);
        setStreamToDelete(null);
      } catch {
        // Error is handled by mutation
      }
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Reorder the full list, not just filtered
      const reorderedIds = arrayMove(
        streams.map((i: Stream) => i.id),
        streams.findIndex((i: Stream) => i.id === active.id),
        streams.findIndex((i: Stream) => i.id === over.id)
      );

      // Call the reorder API
      try {
        await reorderMutation.mutateAsync(reorderedIds);
      } catch {
        // Error is handled by mutation
      }
    }
  };

  const isFormLoading = createMutation.isPending || updateMutation.isPending;
  const isDeleteLoading = deleteMutation.isPending;

  return (
    <Box>
      <PageHeader
        title="Streams"
        subtitle="Manage class streams (e.g. Leisure, Academy). A stream can override the hybrid 1:1 clinic duration. Drag to reorder."
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Streams' },
        ]}
        actionLabel="Add Stream"
        onAction={handleAdd}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load streams. Please try again.
        </Alert>
      )}

      {/* Filters */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 2 }}
        alignItems="center"
      >
        <FormControlLabel
          control={
            <Switch
              checked={hideInactive}
              onChange={(e) => setHideInactive(e.target.checked)}
              size="small"
            />
          }
          label="Hide inactive streams"
        />

        {filteredStreams.length !== streams.length && (
          <Chip
            label={`Showing ${filteredStreams.length} of ${streams.length} streams`}
            size="small"
            variant="outlined"
          />
        )}
      </Stack>

      {/* Streams List */}
      <Paper sx={{ overflow: 'hidden' }}>
        {isLoading ? (
          <List>
            {[1, 2, 3, 4].map((i) => (
              <ListItem key={i} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ mr: 2 }}>
                  <Skeleton variant="rectangular" width={24} height={24} />
                </Box>
                <ListItemText
                  primary={<Skeleton variant="text" width="60%" />}
                  secondary={<Skeleton variant="text" width="30%" />}
                />
              </ListItem>
            ))}
          </List>
        ) : filteredStreams.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {streams.length > 0
                ? 'No streams match the current filter.'
                : "No streams configured. Click 'Add Stream' to create one."}
            </Typography>
          </Box>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filteredStreams.map((i: Stream) => i.id)}
              strategy={verticalListSortingStrategy}
            >
              <List disablePadding>
                {filteredStreams.map((stream: Stream) => (
                  <SortableStreamItem
                    key={stream.id}
                    stream={stream}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </List>
            </SortableContext>
          </DndContext>
        )}
      </Paper>

      {/* Add/Edit Modal */}
      <FormModal
        open={modalOpen}
        title={editingStream ? 'Edit Stream' : 'Add Stream'}
        submitLabel={editingStream ? 'Update' : 'Create'}
        loading={isFormLoading}
        onSubmit={handleSubmit}
        onClose={() => setModalOpen(false)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Stream Name"
            placeholder="e.g., Academy"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            fullWidth
          />
          <TextField
            label="1:1 Clinic Duration (minutes)"
            type="number"
            placeholder="e.g., 45"
            value={formData.individualSlotDuration}
            onChange={(e) => setFormData({ ...formData, individualSlotDuration: e.target.value })}
            fullWidth
            inputProps={{ min: 15, max: 120, step: 5 }}
            helperText="Leave blank to use each class’s own clinic duration. Group lessons are unaffected."
          />
          <ColorPickerField
            label="Color"
            value={formData.color}
            onChange={(color) => setFormData({ ...formData, color })}
            helperText="Optional color for visual identification"
            useSchoolPalette
          />
          {editingStream && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label={formData.isActive ? 'Active' : 'Inactive'}
            />
          )}
        </Box>
      </FormModal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Stream"
        message={`Are you sure you want to delete "${streamToDelete?.name}"? Classes assigned to this stream will have their stream cleared.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={isDeleteLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
}
